import { API_ENDPOINTS } from '@/api/endpoints';
import { useCrumb } from '@/crumbs/useCrumb.ts';
import { useOrg } from '@/organization/useOrg';
import { apiFetch } from '@/util/apiClient';
import {
  alpha,
  Box,
  Button,
  Container,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CustomDivider } from '../components/CustomDivider.tsx';
import { FooterAppBar } from '../components/HeaderAppBar.tsx';
import { Routes } from '../CSIRouter.tsx';
import { useColorWheel } from '../hooks/useColorWheel.ts';
import { Project } from '../stores/ProjectStore.ts';
import {
  SubprojectBuildingDetails,
  useSubprojectStore,
} from '../stores/SubprojectStore.ts';
import { formattedNumber } from '../utils.ts';
import { DecarbHeaderBar } from './components/DecarbHeaderBar.tsx';
import { StageStatusButton } from './components/StageStatusButton';
import { DecarbHubBuildingTile } from './DecarbHubBuildingTile.tsx';
import { getTechnologyStorageKey } from './utils.ts';

export const DecarbonizationHub = () => {
  const subprojectStore = useSubprojectStore();
  const navigate = useNavigate();
  const { projectId, subId } = useParams();
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  const [subprojects, setSubprojects] = useState<SubprojectBuildingDetails[]>(
    [],
  );
  const { getColor } = useColorWheel();
  const { selectedOrg } = useOrg();
  const { addCrumb } = useCrumb();

  // Fetch project data using the organization context
  const { data: project } = useQuery({
    queryKey: ['project', selectedOrg?.id, projectId],
    queryFn: async () => {
      if (!selectedOrg?.id || !projectId) return null;
      return apiFetch<Project>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.DETAIL(selectedOrg.id, projectId),
      );
    },
    enabled: !!selectedOrg?.id && !!projectId,
  });

  useEffect(() => {
    if (!subId || !selectedOrg) {
      return;
    }

    // Fetch subproject data (this runs on mount and when dependencies change)
    subprojectStore
      .fetchSubprojectDetails(subId, selectedOrg.id)
      .then(setSubproject)
      .catch(console.error);
  }, [subprojectStore, subId, selectedOrg]);

  // Refetch data when navigating back to this page
  useEffect(() => {
    const handleFocus = () => {
      if (subId && selectedOrg) {
        subprojectStore
          .fetchSubprojectDetails(subId, selectedOrg.id)
          .then(setSubproject)
          .catch(console.error);
      }
    };

    // Refetch when window regains focus (e.g., user navigates back)
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [subprojectStore, subId, selectedOrg]);

  useEffect(() => {
    if (!projectId || !selectedOrg) {
      return;
    }

    subprojectStore
      .fetchSubprojects(projectId)
      .then(setSubprojects)
      .catch(console.error);
  }, [subprojectStore, projectId, selectedOrg]);

  const refreshSubprojects = useCallback(() => {
    if (!projectId) return;
    subprojectStore
      .fetchSubprojects(projectId)
      .then(setSubprojects)
      .catch(console.error);
  }, [subprojectStore, projectId]);

  const designLifeDisplay =
    subproject?.designLifeYears != null
      ? `${formattedNumber(subproject.designLifeYears)} yrs`
      : null;

  const newOrRenewalDisplay = subproject?.newOrRenewal
    ? subproject.newOrRenewal === 'new'
      ? 'New'
      : 'Renewal'
    : null;

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }


  return (
    <React.Fragment>
      <Container maxWidth='xl' sx={{ px: 0, overflow: 'auto' }}>
        <DecarbHeaderBar
          orgName={selectedOrg?.name || ''}
          projectName={project?.name || ''}
          fiscalYearName={project?.fiscalYearDetails?.name}
          subprojects={subprojects}
          currentSubprojectId={subId}
          projectId={projectId}
          pencilsDown={project?.pencilsDown}
          designApproved={project?.designApproved}
          budgetApproved={project?.budgetApproved}
          onPencilsDownToggle={refreshSubprojects}
          onSubprojectChange={(id) => {
            if (id === 'overview') {
              navigate(`/project/${projectId}/decarb/overview`);
              addCrumb(
                {
                  path: `/project/${projectId}/decarb/overview`,
                  label: 'Overview Decarbonization',
                },
                location.pathname,
              );
              return;
            }
            const selectedSubproject = subprojects.find((sp) => sp.id === id);
            navigate(`/project/${projectId}/decarb/${id}`);
            if (selectedSubproject) {
              addCrumb(
                {
                  path: `/project/${projectId}/decarb/${id}`,
                  label: selectedSubproject.name + ' Decarbonization',
                },
                location.pathname,
              );
            }
          }}
        />
        <Grid
          container
          columnSpacing={{ xs: 1, sm: 2 }}
          rowSpacing={{ xs: 2, sm: 1 }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {subproject && (
              <DecarbHubBuildingTile
                usage={'Conventional'}
                alternative={subproject.conventionalAlternative}
                getColor={getColor}
                designLifeDisplay={designLifeDisplay}
                newOrRenewalDisplay={newOrRenewalDisplay}
                designCost={subproject?.designCost || 0}
                constructionCost={subproject?.constructionCost || 0}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            {subproject && (
              <DecarbHubBuildingTile
                usage={'Low Carbon'}
                alternative={subproject.reducedCarbonAlternative}
                compare={subproject.conventionalAlternative}
                getColor={getColor}
                designLifeDisplay={designLifeDisplay}
                newOrRenewalDisplay={newOrRenewalDisplay}
                designCost={subproject?.designCost || 0}
                constructionCost={subproject?.constructionCost || 0}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            {subproject && (
              <DecarbHubBuildingTile
                usage={'Net Zero'}
                alternative={subproject.zeroCarbonAlternative}
                compare={subproject.conventionalAlternative}
                getColor={getColor}
                designLifeDisplay={designLifeDisplay}
                newOrRenewalDisplay={newOrRenewalDisplay}
                designCost={subproject?.designCost || 0}
                constructionCost={subproject?.constructionCost || 0}
              />
            )}
          </Grid>
        </Grid>
      </Container>
      <SubprojectHubFooter subproject={subproject} />
    </React.Fragment>
  );
};

interface SubprojectHubFooterProps {
  subproject?: SubprojectBuildingDetails;
}

const SubprojectHubFooter: React.FC<SubprojectHubFooterProps> = ({
  subproject,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <React.Fragment>
      <FooterAppBar
        position={'sticky'}
        sx={{
          top: 'auto',
          bottom: 0,
          bgcolor: 'transparent',
          boxShadow: 'none',
          pb: { xs: 1, md: 2 },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'center', md: 'space-between' },
            gap: { xs: 2, md: 0 },
            py: { xs: 1, md: 0 },
          }}
        >
          <Button
            variant={'contained'}
            color={'success'}
            size={isMobile ? 'small' : 'medium'}
            component={Link}
            to={'./' + Routes.Project.Decarbonization.BuildingPurpose.path}
            sx={{
              borderRadius: 1,
              py: { xs: 1.5, md: 3 },
              px: { xs: 3, md: 8 },
              width: { xs: '100%', sm: 'auto' },
              textDecoration: 'none',
            }}
          >
            Space Usage
          </Button>
          {!isMobile && <CustomDivider arrow />}
          <Box
            sx={{
              borderRadius: 1,
              bgcolor: alpha(theme.palette.success.main, 0.4),
              p: { xs: 1, md: 2 },
              display: 'flex',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: { xs: 1, md: 2 },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'center', sm: 'flex-start' },
              '& > button[disabled]': {
                color: 'inherit !important',
              },
            }}
          >
            {(subproject?.enabledTechnologyCategories || []).map(
              (categoryName) => {
                const storageKey = getTechnologyStorageKey(categoryName);

                const hasConventional = Boolean(
                  subproject?.conventionalAlternative?.technologyParams?.[
                    storageKey
                  ]?.params?.length,
                );
                const hasReducedCarbon = Boolean(
                  subproject?.reducedCarbonAlternative?.technologyParams?.[
                    storageKey
                  ]?.params?.length,
                );
                const hasZeroCarbon = Boolean(
                  subproject?.zeroCarbonAlternative?.technologyParams?.[
                    storageKey
                  ]?.params?.length,
                );

                const isComplete =
                  hasConventional && hasReducedCarbon && hasZeroCarbon;

                const routeMap: Record<string, string | undefined> = {
                  HVAC: Routes.Project.Decarbonization.HVACSelection.path,
                  Lighting:
                    Routes.Project.Decarbonization.LightingSelection.path,
                  WaterPumps:
                    Routes.Project.Decarbonization.WaterPumpSelection.path,
                  WaterHeaters:
                    Routes.Project.Decarbonization.WaterHeaterSelection.path,
                  Cooking: Routes.Project.Decarbonization.CookingSelection.path,
                  PlugLoad:
                    Routes.Project.Decarbonization.PlugLoadSelection.path,
                  Monitoring:
                    Routes.Project.Decarbonization.MonitorSelection.path,
                  Specialized:
                    Routes.Project.Decarbonization.SpecializedSelection.path,
                };

                const route = routeMap[categoryName];

                return (
                  <StageStatusButton
                    key={categoryName}
                    label={categoryName}
                    isComplete={isComplete}
                    to={route ? `./${route}` : undefined}
                  />
                );
              },
            )}
          </Box>
          {!isMobile && <CustomDivider arrow />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 },
              alignItems: 'center',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              variant={'contained'}
              color={'success'}
              size={isMobile ? 'small' : 'medium'}
              component={Link}
              to={`./${Routes.Project.Decarbonization.EnergySelection.path}`}
              sx={{
                borderRadius: 1,
                py: { xs: 1.5, md: 3 },
                px: { xs: 3, md: 8 },
                width: { xs: '100%', sm: 'auto' },
                textDecoration: 'none',
              }}
            >
              Energy Sources
            </Button>
          </Box>
        </Toolbar>
      </FooterAppBar>
    </React.Fragment>
  );
};
